import { useRef, useEffect, useCallback } from 'react';
import { PlusCircle } from '@phosphor-icons/react';
import { optimizeImage } from '@/lib/imageOptimization';
import { useSmartImage } from '@/hooks/useSmartImage';
import { useIsMobile } from '@/hooks/use-mobile';

export interface PeopleProfileCardProps {
  /** Profile image URL */
  imageUrl?: string;
  /** Person's name */
  name: string;
  /** Person's title/role */
  title: string;
  /** Click handler for card interaction */
  onClick?: () => void;
  /** Additional className */
  className?: string;
}

/**
 * component-card-people-profile
 * 
 * A profile card for team members following the atomic design token system.
 * 
 * Tokens applied:
 * - Dimensions: w-[280px] md:w-[320px], h-[400px]
 * - Layout: flex flex-col justify-end
 * - Padding: px-6 py-6
 * - Radius: rounded-3xl (24px)
 * - Fallback bg: bg-graphite
 * - Image: object-cover object-top
 * - Gradient: h-1/2 from-black/70 via-black/30 to-transparent
 * - Typography: Name (text-28 bold), Title (text-14)
 * - Action: PlusCircle size={45} weight="fill" at bottom-right
 * - Hover: scale-[1.02] duration-300 ease-out
 */
export function PeopleProfileCard({
  imageUrl,
  name,
  title,
  onClick,
  className = '',
}: PeopleProfileCardProps) {
  const isClickable = !!onClick;
  const { ref, inView } = useSmartImage();
  const isMobile = useIsMobile();
  const preloadLinkRef = useRef<HTMLLinkElement | null>(null);
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup preload link and timer on unmount
  useEffect(() => {
    return () => {
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
      if (preloadLinkRef.current && document.head.contains(preloadLinkRef.current)) {
        document.head.removeChild(preloadLinkRef.current);
      }
    };
  }, []);

  const preloadDialogImage = useCallback(() => {
    if (!imageUrl || preloadLinkRef.current) return;
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
    const dialogWidth = isMobile ? 750 : 800;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = optimizeImage(imageUrl, { width: dialogWidth });
    document.head.appendChild(link);
    preloadLinkRef.current = link;
  }, [imageUrl, isMobile]);

  const scheduleCleanup = useCallback(() => {
    // Delay cleanup by 2s so the preload survives hover-to-click transitions
    cleanupTimerRef.current = setTimeout(() => {
      if (preloadLinkRef.current && document.head.contains(preloadLinkRef.current)) {
        document.head.removeChild(preloadLinkRef.current);
        preloadLinkRef.current = null;
      }
    }, 2000);
  }, []);

  return (
    <div
      ref={ref}
      className={`
        relative
        w-[280px] md:w-[320px]
        h-[400px]
        rounded-3xl overflow-hidden
        bg-graphite
        flex flex-col justify-end
        px-6 py-6
        ${isClickable ? 'cursor-pointer md:hover:scale-[1.02]' : ''}
        transition-transform duration-300 ease-out
        ${className}
      `}
      onClick={onClick}
      onMouseEnter={isClickable ? preloadDialogImage : undefined}
      onMouseLeave={isClickable ? scheduleCleanup : undefined}
      onTouchStart={isClickable ? preloadDialogImage : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
    >
      {/* Profile image */}
      {inView && imageUrl && (
        <img
          src={optimizeImage(imageUrl, { width: 400 })}
          alt={name}
          loading="lazy"
          decoding="async"
          width={320}
          height={400}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      )}

      {/* Bottom gradient for text legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

      {/* Content with button clearance */}
      <div className="relative z-10 pr-16">
        {/* atom-text-profile-name: text-28 font-bold text-platinum */}
        <h3 className="text-28 font-bold text-platinum line-clamp-2 mb-1">
          {name}
        </h3>

        {/* atom-text-profile-title: text-14 text-platinum */}
        <p className="text-14 text-platinum line-clamp-2">
          {title}
        </p>
      </div>

      {/* molecule-card-action-expand: PlusCircle at bottom-right */}
      {isClickable && (
        <PlusCircle
          size={45}
          weight="fill"
          className="text-platinum absolute bottom-4 right-4 z-10"
        />
      )}
    </div>
  );
}

export default PeopleProfileCard;
